/* eslint-disable no-nested-ternary */
import { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import { TextField } from 'formik-mui';
import IconButton from '@mui/material/IconButton';
import Link from 'next/link';
import * as Yup from 'yup';
import FormHelperText from '@mui/material/FormHelperText';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { contactUsDomain } from '../../domain'

import { eventService } from '../../services/homii-services';
import Button from './button.component';
import useResponsive from '../../../utils/responsive';

const newsletterInitialValues = {
    email: '',
};
const newsletterValidationSchema = Yup.object({
    email: Yup.string('').email('Enter a valid email').required('Email is required'),
});

const Footer = () => {    
    const [newsletterSubmitError, setNewsletterSubmitError] = useState(null);
    const [successfullySubmitted, setSuccessfullySubmitted] = useState(null);
    const { isVerySmallMobile } = useResponsive();

    const onFacebookClick = () => {
        eventService.logCustomEvent('facebook_click_footer', {
            description: 'Clicked on Facebook from footer',
        });
        window.open('//www.facebook.com/Homiilifestyle/', '_blank');
    };

    const onInstagramClick = () => {
        eventService.logCustomEvent('instagram_click_footer', {
            description: 'Clicked on Instragram from footer',
        });
        window.open('//www.instagram.com/homiilifestyle/?hl=en', '_blank');
    };

    const onYouTubeClick = () => {
        eventService.logCustomEvent('youtube_click_footer', {
            description: 'Clicked on YouTube from footer',
        });
        window.open('https://www.youtube.com/@homiilifestyle1617', '_blank');
    };

    const onLinkedInClick = () => {
        eventService.logCustomEvent('linkedin_click_footer', {
            description: 'Clicked on LinkedIn from footer',
        });
        window.open('https://www.linkedin.com/company/homiilifestyle/', '_blank');
    };

    const onSubmit = async ({ email }, { setSubmitting }) => {
        setSubmitting(true);
        try {
            await contactUsDomain.subscribeToNewsletter(email);
            setNewsletterSubmitError(null);
            setSuccessfullySubmitted(true);
        } catch (error) {
            setNewsletterSubmitError(error.message);
            setSuccessfullySubmitted(false);
        } finally {
            setSubmitting(false);
        }
    };

    const currentYear = new Date().getFullYear();
    const newsletterSignUpTitle = !successfullySubmitted
        ? 'Get the latest HOMii happenings. Sign up here.'
        : 'Thanks for signing up! We’ll keep in touch.';

    const newsletterSignUpBody = !successfullySubmitted
        ? ["What's new? What's hot?", <br key={1} />, 'Stay in the know with all things HOMii.']
        : 'Stay tuned for the latest HOMii happenings';

    const renderFirstTwoButtons = () => {
        return (
            <>
                <a
                    aria-label="google play"
                    rel="noreferrer"
                    href="//play.google.com/store/apps/details?id=za.co.homii.mobile&hl=en"
                    target="_blank"
                >
                    <img
                        src="/assets/images/google_play.png"
                        alt="Google Play"
                        className="app-download-buttons"
                    />
                </a>

                <a
                    aria-label="apple store"
                    href="//apps.apple.com/za/app/homii-lifestyle/id1478894335"
                    rel="noreferrer"
                    target="_blank"
                >
                    <img
                        src="/assets/images/apple.png"
                        alt="Play Store"
                        className="app-download-buttons"
                    />
                </a>
            </>
        );
    };
    return (

        <div className="footer-wrapper">
            <div className="footer-container container-root container-root-maxWidthLg">

                <div className="footer-grid-container footer-grid-bottom">
                    <div className="footer-grid-item footer-grid-md-6 footer-grid-sm-12 footer-grid-xs-12">
                        <h4 className="footer-header-h4">
                            {newsletterSignUpTitle}
                        </h4>
                        <p className="footer-light-text">
                            {newsletterSignUpBody}
                        </p>
                        {!successfullySubmitted && (
                            <Formik
                                initialValues={newsletterInitialValues}
                                validationSchema={newsletterValidationSchema}
                                onSubmit={onSubmit}
                                validateOnBlur
                                validateOnChange={false}
                            >
                                {({
                                    submitForm,
                                    isSubmitting,
                                    touched,
                                    errors,
                                    handleChange,
                                    handleBlur,
                                }) => (
                                    <Form>
                                        <Field
                                            component={TextField}
                                            required
                                            name="email"
                                            id="footer-email-address"
                                            label="Email Address"
                                            type="email"
                                            variant="outlined"
                                            color="secondary"
                                            helperText={touched.email ? errors.email : ''}
                                            error={touched.email && !!errors.email}
                                            onChange={handleChange}
                                            onBlur={handleBlur}    
                                            className="footer-text-field"                                        
                                        />
                                        <FormHelperText error>
                                            {newsletterSubmitError}
                                        </FormHelperText>
                                        <Button
                                            variant="contained"
                                            className="green-button"
                                            id="footer-submit-button"
                                            isLoading={isSubmitting}
                                            onClick={submitForm}
                                        >
                                            Sign Up
                                        </Button>
                                    </Form>
                                )}
                            </Formik>
                        )}
                    </div>

                    <div className="footer-grid-container footer-grid-item footer-grid-md-6 footer-grid-xs-12 footer-grid-sm-12 footer-links">
                        <div className="footer-grid-item footer-grid-sm-4 footer-grid-xs-12">
                            <h5 className='footer-header-h5' style={{ marginBottom: '30px' }}>HOMii</h5>
                            <ul className="footer-list">
                                <li className="footer-list-item">
                                    <Link href="/blog" as="/blog" className='footer-link' id='footer-blog'>
                                        Blog                                        
                                    </Link>
                                </li>
                                <li className="footer-list-item">
                                    <Link href="/connect-with-us" as="/connect-with-us" className='footer-link' id='footer-contact-us'>                                        
                                        Contact Us                                        
                                    </Link>
                                </li>
                                <li className="footer-list-item">
                                    <Link href="/faq" as="/faq" className='footer-link'>
                                        FAQs
                                    </Link>
                                </li>
                            </ul>                           
                        </div>
                        <div className="footer-grid-item footer-grid-sm-4 footer-grid-xs-12">
                            <h5 className='footer-header-h5' style={{ marginBottom: '30px' }}>Corporate</h5>
                            <ul className="footer-list">
                                <li className="footer-list-item">
                                    <Link href="/partners#main" as="/partners#main" className='footer-link'>
                                        Partners
                                    </Link>
                                </li>
                                <li className="footer-list-item">
                                    <Link href="/partners#investors" as="/partners#investors" className='footer-link'>
                                        Investors
                                    </Link>
                                </li>
                                <li className="footer-list-item">
                                    <Link href="/corporate-bookings" as="/corporate-bookings" className='footer-link'>
                                        Corporate Bookings
                                    </Link>
                                </li>
                                <li className="footer-list-item">
                                    <Link
                                        href="/partners#commercial-rental"
                                        as="/partners#commercial-rental" className='footer-link'
                                    >
                                        Commercial Rental
                                    </Link>
                                </li>
                            </ul>
                        </div>
                        <div className="footer-grid-item footer-grid-sm-4 footer-grid-xs-12">
                            <h5 className='footer-header-h5' style={{ marginBottom: '30px' }}>Legal</h5>
                            <ul className="footer-list">
                                <li className="footer-list-item">
                                    <Link href="/privacy-policy" as="/privacy-policy" className="footer-link">
                                        Privacy Policy
                                    </Link>
                                </li>
                                <li className="footer-list-item">
                                    <Link href="/terms-and-conditions" as="/terms-and-conditions" className="footer-link">
                                        Terms and Conditions
                                    </Link>
                                </li>
                                <li className="footer-list-item">
                                    <Link href="/notice-per-popia" as="/notice-per-popia" className="footer-link">
                                        Notice per POPIA
                                    </Link>
                                </li>
                            </ul>
                        </div>
                        <div className="footer-border" />
                    </div>
                </div>

                <div className="footer-grid-container footer-grid-bottom">
                    <div className="footer-grid-item footer-grid-sm-6 footer-grid-xs-12">
                        <h5 className='footer-header-h5' style={{ marginBottom: '30px' }}>Get the App</h5>
                        <div className='footer-app-buttons-container'>
                            {isVerySmallMobile && renderFirstTwoButtons()}
                            {!isVerySmallMobile && (
                                <div className='footer-row-button'>{renderFirstTwoButtons()}</div>
                            )}
                            <a
                                aria-label="huawei appgallery"
                                href="//appgallery.huawei.com/#/app/C102716343"
                                rel="noreferrer"
                                target="_blank"
                            >
                                <img
                                    src="/assets/images/huawei.png"
                                    alt="Huawei Store"
                                    className="app-download-buttons"
                                />
                            </a>
                        </div>
                    </div>
                    <div id="footer-social" className="footer-grid-item footer-grid-sm-6 footer-grid-xs-12 footer-grid-social">
                        <h5 className="footer-header-h5" style={{ marginBottom: '18px' }}>Social</h5>
                        <IconButton
                            className="socials-button"
                            id="footer-link-facebook"
                            onClick={onFacebookClick}
                        >
                            <img
                                src="/assets/images/facebook_icon.png"
                                alt="Facebook"
                                className="socials-image"
                            />
                        </IconButton>
                        <IconButton
                            className="socials-button"
                            id="footer-link-instagram"
                            alt="Instagram"
                            onClick={onInstagramClick}
                        >
                            <InstagramIcon style={{ color: 'white' }}/>
                        </IconButton>
                        <IconButton
                            className="socials-button"
                            id="footer-link-youtube"
                            alt="YouTube"
                            onClick={onYouTubeClick}
                        >
                            <YouTubeIcon style={{ color: 'white' }}/>
                        </IconButton>
                        <IconButton
                            className="socials-button"
                            id="footer-link-linkedin"
                            alt="LinkedIn"
                            onClick={onLinkedInClick}
                        >
                            <LinkedInIcon style={{ color: 'white' }}/>
                        </IconButton>                        
                    </div>
                </div>

                <div className="footer-grid-item footer-grid-xs-12 footer-sub footer-flex-container">
                    <p
                        className="footer-copyright-p footer-copyright-text"
                        variant="subheading"
                        component="span"
                    >
                        © Copyright HOMii {currentYear}
                    </p>
                    <p
                        className="footer-copyright-p footer-copyright-p-end footer-copyright-text"
                        variant="subheading"
                        component="span"                        
                    >
                        All rights reserved.
                    </p>
                </div>

            </div>
        </div>
    );
};

export default Footer;
